/**
 * Updated Assessment Service using UnifiedApiService
 * 
 * This is an example of how to migrate from direct fetch calls
 * to using the unified API service for consistent token handling.
 */

import { BaseService } from '../base/BaseService';
import { IAssessmentService } from '../../interfaces/IAssessmentService';
import { 
  Assessment, 
  AssessmentTemplate, 
  AssessmentResponse,
  Question,
  PaginatedResponse 
} from '../../../types/assessment';
import { unifiedApi, handleApiError } from '../../UnifiedApiService';

export class ApiAssessmentService extends BaseService implements IAssessmentService {
  constructor(private apiUrl: string) {
    super('ApiAssessmentService');
  }

  async getAssessments(page: number = 1, limit: number = 10): Promise<PaginatedResponse<Assessment>> {
    return this.withCache(
      `assessments-${page}-${limit}`,
      async () => {
        try {
          // Use unified API service instead of direct fetch
          const data = await unifiedApi.get<any>(
            `/assessments/?page=${page}&limit=${limit}`
          );
          
          // Handle array response from backend
          if (Array.isArray(data)) {
            // Backend returns array directly, convert to paginated response
            const assessments = data.map(item => ({
              ...item,
              title: item.name || item.title,
              isPublic: item.is_public !== false,
              createdAt: item.created_at || item.createdAt,
              updatedAt: item.updated_at || item.updatedAt,
              questions: item.questions || []
            }));
            
            return {
              items: assessments,
              results: assessments,
              total: assessments.length,
              page: page,
              limit: limit,
              totalPages: 1,
              hasNext: false,
              hasPrevious: false
            };
          }
          
          // Handle paginated response
          return {
            items: data.results || data.items || [],
            results: data.results || data.items || [],
            total: data.count || data.total || 0,
            page: data.page || page,
            limit: data.limit || limit,
            totalPages: data.total_pages || Math.ceil((data.count || 0) / limit),
            hasNext: data.has_next || false,
            hasPrevious: data.has_previous || false
          };
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to fetch assessments: ${message}`);
        }
      }
    );
  }

  async getAssessment(id: string): Promise<Assessment> {
    return this.withCache(
      `assessment-${id}`,
      async () => {
        try {
          const data = await unifiedApi.get<any>(`/assessments/${id}/`);
          
          return {
            ...data,
            title: data.name || data.title,
            isPublic: data.is_public !== false,
            createdAt: data.created_at || data.createdAt,
            updatedAt: data.updated_at || data.updatedAt,
            questions: data.questions || []
          };
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to fetch assessment: ${message}`);
        }
      }
    );
  }

  async createAssessment(assessment: Partial<Assessment>): Promise<Assessment> {
    try {
      const payload = {
        ...assessment,
        name: assessment.title,
        template_id: assessment.templateId,  // Backend expects 'template_id' for creation
        is_public: assessment.isPublic
      };
      // Remove frontend-specific fields
      delete payload.title;
      delete payload.templateId;
      delete payload.isPublic;
      delete payload.questions;  // Questions are in the template, not the assessment
      
      const data = await unifiedApi.post<any>('/assessments/', payload);
      
      // Clear cache after creation
      this.clearCache();
      
      return {
        ...data,
        title: data.name || data.title,
        isPublic: data.is_public !== false,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to create assessment: ${message}`);
    }
  }

  async updateAssessment(id: string, assessment: Partial<Assessment>): Promise<Assessment> {
    try {
      const payload = {
        ...assessment,
        name: assessment.title,
        is_public: assessment.isPublic
      };
      
      // Use PUT to ensure the backend update() method is called with questions
      const data = await unifiedApi.put<any>(`/assessments/${id}/`, payload);
      
      // Clear cache after update
      this.clearCache(`assessment-${id}`);
      this.clearCache();
      
      return {
        ...data,
        title: data.name || data.title,
        isPublic: data.is_public !== false,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to update assessment: ${message}`);
    }
  }

  async deleteAssessment(id: string): Promise<void> {
    try {
      await unifiedApi.delete(`/assessments/${id}/`);
      
      // Clear cache after deletion
      this.clearCache(`assessment-${id}`);
      this.clearCache();
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to delete assessment: ${message}`);
    }
  }

  async getTemplates(category?: string): Promise<AssessmentTemplate[]> {
    const cacheKey = category ? `templates-${category}` : 'templates-all';
    
    return this.withCache(
      cacheKey,
      async () => {
        try {
          const url = category 
            ? `/assessments/templates/?category=${category}`
            : '/assessments/templates/';
          
          const data = await unifiedApi.get<any>(url);
          
          // Handle both array and paginated responses
          const templates = Array.isArray(data) ? data : (data.results || data.items || []);
          
          return templates.map((template: any) => ({
            ...template,
            createdAt: template.created_at || template.createdAt,
            updatedAt: template.updated_at || template.updatedAt
          }));
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to fetch templates: ${message}`);
        }
      }
    );
  }

  async getTemplate(id: string): Promise<AssessmentTemplate> {
    return this.withCache(
      `template-${id}`,
      async () => {
        try {
          const data = await unifiedApi.get<any>(`/assessments/templates/${id}/`);
          
          return {
            ...data,
            createdAt: data.created_at || data.createdAt,
            updatedAt: data.updated_at || data.updatedAt
          };
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to fetch template: ${message}`);
        }
      }
    );
  }

  async createTemplate(template: Omit<AssessmentTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<AssessmentTemplate> {
    try {
      const data = await unifiedApi.post<any>('/assessments/templates/', template);
      
      this.clearCache();
      
      return {
        ...data,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to create template: ${message}`);
    }
  }

  async updateTemplate(id: string, updates: Partial<AssessmentTemplate>): Promise<AssessmentTemplate> {
    try {
      const data = await unifiedApi.patch<any>(`/assessments/templates/${id}/`, updates);
      
      this.clearCache();
      
      return {
        ...data,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to update template: ${message}`);
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    try {
      await unifiedApi.delete(`/assessments/templates/${id}/`);
      this.clearCache();
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to delete template: ${message}`);
    }
  }

  async getResponses(assessmentId: string): Promise<AssessmentResponse[]> {
    return this.withCache(
      `responses-${assessmentId}`,
      async () => {
        try {
          const data = await unifiedApi.get<any>(`/assessments/responses/?assessment=${assessmentId}`);
          
          const responses = Array.isArray(data) ? data : (data.results || data.items || []);
          
          return responses.map((response: any) => ({
            ...response,
            submittedAt: response.submitted_at || response.submittedAt,
            createdAt: response.created_at || response.createdAt,
            updatedAt: response.updated_at || response.updatedAt
          }));
        } catch (error: any) {
          const errorDetails = {
            message: 'Failed to fetch assessment responses',
            component: 'ApiAssessmentService',
            method: 'getResponses',
            assessmentId: assessmentId,
            endpoint: `/assessments/responses/?assessment=${assessmentId}`,
            originalError: error.message || String(error),
            stack: error.stack,
            timestamp: new Date().toISOString()
          };
          console.error('[ApiAssessmentService] Error:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        }
      }
    );
  }

  async getResponse(id: string): Promise<AssessmentResponse> {
    return this.withCache(
      `response-${id}`,
      async () => {
        try {
          const data = await unifiedApi.get<any>(`/assessments/responses/${id}/`);
          
          return {
            ...data,
            submittedAt: data.submitted_at || data.submittedAt,
            createdAt: data.created_at || data.createdAt,
            updatedAt: data.updated_at || data.updatedAt
          };
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to fetch response: ${message}`);
        }
      }
    );
  }

  async submitResponse(response: Partial<AssessmentResponse>): Promise<AssessmentResponse> {
    try {
      const data = await unifiedApi.post<any>('/assessments/responses/', response);
      
      return {
        ...data,
        submittedAt: data.submitted_at || data.submittedAt,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to submit response: ${message}`);
    }
  }

  async updateResponse(id: string, updates: Partial<AssessmentResponse>): Promise<AssessmentResponse> {
    try {
      const data = await unifiedApi.patch<any>(`/assessments/responses/${id}/`, updates);
      
      this.clearCache();
      
      return {
        ...data,
        submittedAt: data.submitted_at || data.submittedAt,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to update response: ${message}`);
    }
  }

  async getQuestions(assessmentId: string): Promise<Question[]> {
    return this.withCache(
      `questions-${assessmentId}`,
      async () => {
        try {
          // Get questions from the assessment itself
          const assessment = await this.getAssessment(assessmentId);

          // Questions are embedded in the assessment
          if (assessment.questions && assessment.questions.length > 0) {
            return assessment.questions;
          }

          // If no questions, this indicates a data integrity issue
          const errorDetails = {
            message: 'Assessment has no questions configured',
            component: 'ApiAssessmentService',
            method: 'getQuestions',
            assessmentId: assessmentId,
            assessmentTitle: assessment.title,
            timestamp: new Date().toISOString(),
            hint: 'Assessment may be incomplete or questions not loaded properly'
          };
          console.error('[ApiAssessmentService] No questions found:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        } catch (error: any) {
          // Re-throw if already a formatted error
          if (error.message?.startsWith('{')) {
            throw error;
          }

          const errorDetails = {
            message: 'Failed to fetch assessment questions',
            component: 'ApiAssessmentService',
            method: 'getQuestions',
            assessmentId: assessmentId,
            originalError: error.message || String(error),
            stack: error.stack,
            timestamp: new Date().toISOString()
          };
          console.error('[ApiAssessmentService] Error:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        }
      }
    );
  }

  async analyzeResponses(assessmentId: string): Promise<any> {
    return this.withCache(
      `analysis-${assessmentId}`,
      async () => {
        try {
          const data = await unifiedApi.get(`/assessments/${assessmentId}/results/`);
          return data;
        } catch (error) {
          const message = handleApiError(error);
          throw new Error(`Failed to analyze responses: ${message}`);
        }
      }
    );
  }

  async getAssessmentQuestions(assessmentId: string): Promise<Question[]> {
    return this.getQuestions(assessmentId);
  }

  async getCurrentUserResponse(assessmentId: string): Promise<AssessmentResponse | null> {
    try {
      const data = await unifiedApi.get<any>(`/assessments/responses/current/?assessment=${assessmentId}`);
      
      if (!data) return null;
      
      return {
        ...data,
        submittedAt: data.submitted_at || data.submittedAt,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt
      };
    } catch (error) {
      console.warn('No current user response found:', error);
      return null;
    }
  }

  async createResponse(response: Partial<AssessmentResponse>): Promise<AssessmentResponse> {
    return this.submitResponse(response);
  }

  // AI-powered question suggestion
  async suggestQuestions(context: {
    ministry_id?: string;
    department?: string;
    target_group?: string;
    context_description?: string;
    num_questions?: number;
  }): Promise<{
    success: boolean;
    questions: Array<{
      text: string;
      question_type: string;
      options: string[];
      help_text: string;
      required: boolean;
      order: number;
    }>;
    context: any;
  }> {
    try {
      const data = await unifiedApi.post('/assessments/suggest_questions/', context);
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to generate question suggestions: ${message}`);
    }
  }

  // Auto-save assessment data
  async autoSaveAssessment(assessmentId: string, data: {
    questions?: any[];
    auto_save_data?: any;
  }): Promise<{
    success: boolean;
    message: string;
    last_auto_save: string;
  }> {
    try {
      const response = await unifiedApi.post(`/assessments/${assessmentId}/auto_save/`, data);
      return response;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to auto-save assessment: ${message}`);
    }
  }

  // Create draft assessment for AI workflow
  async createDraft(data: {
    title?: string;
    description?: string;
    ministry_id?: string;
    department?: string;
    target_group?: string;
    context_description?: string;
    visual_template_id?: string;
  }): Promise<{
    assessment: Assessment;
    template_id: string;
  }> {
    try {
      const response = await unifiedApi.post('/assessments/create_draft/', {
        title: data.title || 'New Assessment',
        description: data.description || '',
        ministry_id: data.ministry_id,
        department: data.department,
        target_group: data.target_group,
        context_description: data.context_description,
        visual_template_id: data.visual_template_id
      });
      return response;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to create draft assessment: ${message}`);
    }
  }

  // Restore auto-saved data
  async restoreAutoSave(assessmentId: string): Promise<{
    success: boolean;
    auto_save_data: any;
    last_auto_save: string | null;
  }> {
    try {
      const data = await unifiedApi.get(`/assessments/${assessmentId}/restore_auto_save/`);
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to restore auto-save: ${message}`);
    }
  }

  // Visual Template methods
  async getVisualTemplates(): Promise<any[]> {
    try {
      const data = await unifiedApi.get('/assessments/visual-templates/');
      return Array.isArray(data) ? data : (data.results || data.items || []);
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to fetch visual templates: ${message}`);
    }
  }

  async getDefaultVisualTemplate(): Promise<any> {
    try {
      const data = await unifiedApi.get('/assessments/visual-templates/get_default/');
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to fetch default visual template: ${message}`);
    }
  }

  async createVisualTemplate(template: any): Promise<any> {
    try {
      const data = await unifiedApi.post('/assessments/visual-templates/', template);
      this.clearCache();
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to create visual template: ${message}`);
    }
  }

  async updateVisualTemplate(id: string, template: any): Promise<any> {
    try {
      const data = await unifiedApi.put(`/assessments/visual-templates/${id}/`, template);
      this.clearCache();
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to update visual template: ${message}`);
    }
  }

  async deleteVisualTemplate(id: string): Promise<void> {
    try {
      await unifiedApi.delete(`/assessments/visual-templates/${id}/`);
      this.clearCache();
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to delete visual template: ${message}`);
    }
  }

  async setDefaultVisualTemplate(id: string): Promise<any> {
    try {
      const data = await unifiedApi.post(`/assessments/visual-templates/${id}/set_default/`);
      this.clearCache();
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to set default visual template: ${message}`);
    }
  }

  async applyVisualTemplate(templateId: string, assessmentId: string): Promise<any> {
    try {
      const data = await unifiedApi.post(`/assessments/visual-templates/${templateId}/apply_to_assessment/`, {
        assessment_id: assessmentId
      });
      this.clearCache();
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to apply visual template: ${message}`);
    }
  }

  async generateReport(assessmentId: string): Promise<any> {
    try {
      // Call backend API to generate assessment report
      const data = await unifiedApi.get(`/assessments/${assessmentId}/report/`);
      return data;
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to generate report: ${message}`);
    }
  }

  async updateAssessmentStatus(assessmentId: string, status: 'draft' | 'active' | 'closed' | 'archived'): Promise<Assessment> {
    try {
      const data = await unifiedApi.post<any>(`/assessments/assessments/${assessmentId}/update-status/`, { status });

      this.clearCache();

      return {
        ...data,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt,
        startDate: data.start_date || data.startDate,
        endDate: data.end_date || data.endDate
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to update assessment status: ${message}`);
    }
  }

  /**
   * WF-009: Close an active assessment using the dedicated close endpoint.
   * This is an explicit action that changes status to 'closed' and prevents
   * new responses from being submitted.
   */
  async closeAssessment(assessmentId: string): Promise<Assessment> {
    try {
      const data = await unifiedApi.post<any>(`/assessments/assessments/${assessmentId}/close/`);

      this.clearCache();

      return {
        ...data,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt,
        startDate: data.start_date || data.startDate,
        endDate: data.end_date || data.endDate
      };
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to close assessment: ${message}`);
    }
  }

  async createBatchResponses(assessmentId: string, count: number): Promise<void> {
    try {
      // Get assessment questions first
      const questions = await this.getQuestions(assessmentId);

      // Create responses sequentially (to avoid overwhelming the server)
      for (let i = 0; i < count; i++) {
        // Step 1: Create empty response
        const createData = await unifiedApi.post<any>('/assessments/responses/', {
          assessment_id: assessmentId,
          anonymous: true,
          anonymous_id: `test-response-${Date.now()}-${i}`
        });

        const responseId = createData.id;

        // Step 2: Save answers for each question
        for (const question of questions) {
          let answerValue: any;

          switch (question.type) {
            case 'scale':
              answerValue = Math.floor(Math.random() * 5) + 1;
              break;
            case 'boolean':
              answerValue = Math.random() > 0.5;
              break;
            case 'select':
              if (question.options && question.options.length > 0) {
                const randomIndex = Math.floor(Math.random() * question.options.length);
                answerValue = question.options[randomIndex].value;
              }
              break;
            case 'multiselect':
              if (question.options && question.options.length > 0) {
                const selectedOptions = question.options
                  .filter(() => Math.random() > 0.5)
                  .map(opt => opt.value);
                answerValue = selectedOptions;
              }
              break;
            case 'text':
              answerValue = `Test response ${i + 1}`;
              break;
            case 'textarea':
              answerValue = `This is a longer test response ${i + 1} with more detailed information.`;
              break;
            default:
              answerValue = `Test answer ${i + 1}`;
          }

          // Save answer
          await unifiedApi.post(`/assessments/responses/${responseId}/save_answer/`, {
            question_id: question.id,
            answer: answerValue
          });
        }

        // Step 3: Mark response as complete
        await unifiedApi.post(`/assessments/responses/${responseId}/complete/`, {});
      }

      console.log(`Created ${count} test responses for assessment ${assessmentId}`);
    } catch (error) {
      const message = handleApiError(error);
      throw new Error(`Failed to create batch responses: ${message}`);
    }
  }

  // No longer need this method - UnifiedApiService handles auth
  // private getHeaders(): HeadersInit { ... }
}